import React, { useMemo, useCallback, useState } from 'react';
import { Card, Table, Checkbox, Space, Typography, Tooltip, Collapse } from 'antd';
import { useDispatch, useSelector } from 'react-redux';
import { InfoCircleOutlined, CaretRightOutlined } from '@ant-design/icons';
import { distActions } from '../../store/dist.slice';
import { useTranslation } from 'react-i18next';

const { Panel } = Collapse;

const AutopublicationCard = () => {
	const dispatch = useDispatch();
	const selectedEmployees = useSelector((state) => state.dist.selected_employees || []);
	const flags = useSelector((state) => state.dist.autopublishByEmployee || {});
	const excludedAutopublish = useSelector((state) => state.dist.excludedAutopublishEmployees || []);
	const { t } = useTranslation();

	// State to track collapse panel
	const [activePanel, setActivePanel] = useState(['1']); // Default open

	const employeeIds = useMemo(
		() => selectedEmployees.map((employee) => employee.id),
		[selectedEmployees],
	);

	const allChecked = useMemo(
		() => employeeIds.length > 0 && employeeIds.every((id) => !!flags[id]),
		[employeeIds, flags],
	);
	const someChecked = useMemo(
		() => employeeIds.some((id) => !!flags[id]) && !allChecked,
		[employeeIds, flags, allChecked],
	);

	const onToggleAll = useCallback(
		(e) => {
			dispatch(distActions.setAutopublishForMany({ employeeIds, enabled: e.target.checked }));
		},
		[dispatch, employeeIds],
	);

	const onToggleOne = useCallback(
		(employeeId, checked) => {
			dispatch(distActions.setEmployeeAutopublish({ employeeId, enabled: checked }));
		},
		[dispatch],
	);

	const columns = useMemo(
		() => [
			{
				title: t('postDistribution.cards.autopublicationUser'),
				dataIndex: 'fullName',
				key: 'fullName',
				ellipsis: true,
			},
			{
				title: (
					<Space size={4}>
						<Typography.Text strong style={{ marginRight: 4 }}>
							{t('postDistribution.cards.autopublicationAutopublish')}
						</Typography.Text>
						<Tooltip title={t('postDistribution.cards.autopublicationHint')}>
							<InfoCircleOutlined />
						</Tooltip>
						<Checkbox indeterminate={someChecked} checked={allChecked} onChange={onToggleAll} />
					</Space>
				),
				dataIndex: 'autopublish',
				key: 'autopublish',
				width: 180,
				render: (_, record) => (
					<Checkbox
						checked={!!flags[record.id]}
						onChange={(e) => onToggleOne(record.id, e.target.checked)}
					/>
				),
			},
		],
		[flags, someChecked, allChecked, onToggleAll, onToggleOne, t],
	);

	// Handle collapse change
	const handleCollapseChange = (keys) => {
		setActivePanel(keys);
	};

	return (
		<Collapse
			activeKey={activePanel}
			onChange={handleCollapseChange}
			expandIcon={({ isActive }) => <CaretRightOutlined rotate={isActive ? 90 : 0} />}
			className="shadow-sm"
		>
			<Panel
				header={
					<Typography.Title level={5} style={{ margin: 0 }}>
						{t('postDistribution.cards.autopublicationPerUser')}
					</Typography.Title>
				}
				key="1"
			>
				<Table
					size="small"
					rowKey={(rec) => String(rec.id)}
					columns={columns}
					dataSource={selectedEmployees}
					pagination={false}
					locale={{
						emptyText: 'No selected users yet',
					}}
				/>
			</Panel>
		</Collapse>
	);
};

export default AutopublicationCard;
